import DetailItem from "./DetailItem";
import { Dispatch, Fragment, SetStateAction, useState } from "react";
import { Button, colors, customEvent, HStack, VStack } from "goi_common";
import { ceilPrice } from "@/utils/ceilPrice";
import { useRouter } from "next/router";
import parlors_discount from "@/constants/parlors_discount_detail.json";

export interface DetailPriceProps {
  path?: "parlor_detail";
  isNotLocale?: boolean;
  results: {
    [key: string]: {
      sum: number | undefined;
      results: {
        [key: string]: {
          detail?: string;
          charge?: number | string;
          has_alpha?: boolean;
        };
      };
    };
  };
}

function DetailPrice({ path, isNotLocale, results }: DetailPriceProps) {
  const [currentTab, setCurrentTab] = useState<"시설" | "음식" | "기타">("시설");

  const router = useRouter();
  const { id } = router.query;

  const discountIdList = parlors_discount.map((res) => res.parlor_id);
  const discountParlor = parlors_discount.filter((res) => res.parlor_id === Number(id))[0];

  const pointList = {
    시설: {
      id: "01",
      content:
        discountIdList.includes(Number(id)) && discountParlor.discount_percent !== 0 ? (
          <>
            해당 장례식장은 상담 진행 후 고이 상조 서비스 이용시{" "}
            <span className="subtitle_14_b white">
              빈소 {discountParlor.discount_percent}% 제휴 할인 가능한 장례식장
            </span>
            입니다.
            <br /> 합리적으로 치를 수 있도록 할인받을 수 있는 법을 상담 받으세요.
          </>
        ) : (
          <>
            비슷한 시설 수준과 접근성을 가진 더 저렴한 장례식장 혹은 해당 장례식장 내에서 더
            저렴하게 할 수 있는 방법을 알려드립니다.
          </>
        ),
    },
    음식: {
      id: "02",
      content: (
        <>
          음식 비용은 신경쓰지 않으면 불필요한 지출이 발생할 수 있어요.
          <br /> 불필요한 지출이 발생하지 않도록 고이 상조 서비스를 통하여 미리 대비하세요.
        </>
      ),
    },
    기타: {
      id: "03",
      content: (
        <>
          제단꽃도 무시할 수 없는 가격대에요. 제단꽃을 더 퀄리티 높고 저렴하게 하고 싶다면, 반드시
          고이 상조 서비스와 상담을 진행하여 합리적인 장례를 만드세요.
        </>
      ),
    },
  };

  return (
    <VStack padding="0 16px">
      <HStack>
        {Object.entries(results).map(([title, { sum }]) => (
          <DetailPriceTab
            key={sum + "tab"}
            currentTab={currentTab}
            setCurrentTab={setCurrentTab}
            title={title}
            sum={sum ? String(ceilPrice(sum, 100000, 10)) : ""}
          />
        ))}
      </HStack>
      {Object.entries(results).map(([title, { sum, results }]) => (
        <Fragment key={title}>
          <DetailItem
            title={title}
            sum={sum}
            isNotLocale={isNotLocale}
            results={results}
            currentTab={currentTab}
          />
        </Fragment>
      ))}
      <VStack background={colors.gray900} padding="0 20px 36px" borderRadius="0 0 5px 5px" gap={24}>
        <VStack background={colors.gray800} padding="20px 16px" gap={14} className="white">
          <div className="subtitle_14_sb">비용 아끼기 포인트 {pointList[currentTab].id}</div>
          <div className="body_14_r gray300">{pointList[currentTab].content}</div>
        </VStack>
        <VStack width="100%" alignItems="center" justifyContent="center">
          <Button
            onClick={() => {
              customEvent({
                newGtm: {
                  path: path || "parlor_detail_price",
                  trigger_and_target: "discount_btn-uv_mtch_rqst_prlr",
                },
              });
              router.push("/match/request/?type=parlor");
            }}
            className="button_14_sb"
            css={{
              padding: "10px 24px",
              background: colors.orange650,
              color: colors.white,
              borderRadius: "5px",
            }}
          >
            {currentTab === "시설" && "빈소 이용료 할인 알아보기"}
            {currentTab === "음식" && "음식 비용 할인 알아보기"}
            {currentTab === "기타" && "기타 비용 할인 알아보기"}
          </Button>
        </VStack>
      </VStack>
    </VStack>
  );
}

export default DetailPrice;

function DetailPriceTab({
  path,
  currentTab,
  setCurrentTab,
  title,
  sum,
}: {
  path?: "parlor_detail";
  currentTab: string;
  setCurrentTab: Dispatch<SetStateAction<any>>;
  title: string;
  sum?: string;
}) {
  return (
    <VStack alignItems="center" justifyContent="center" width="33.3%" gap={8}>
      <div className="subtitle_14_sb gray900">{title} 예상 비용</div>
      <HStack
        gap={3}
        padding="19px 0"
        borderRadius="5px 5px 0 0"
        width="100%"
        alignItems="center"
        justifyContent="center"
        css={{
          background: title === currentTab ? colors.gray900 : colors.gray50,
          color: title === currentTab ? colors.white : colors.gray600,
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.25)",
          cursor: "pointer",
        }}
        onClick={() => {
          customEvent({
            newGtm: {
              path: path || "parlor_detail_price",
              trigger_and_target: path ? "tab-expected-detail-price" : "tab-price",
            },
          });
          setCurrentTab(title);
        }}
      >
        {!sum || sum === "0" ? (
          "-"
        ) : (
          <>
            <div className="caption_12_sb">약</div>
            <div className="subtitle_14_sb">{sum}만원</div>
          </>
        )}
      </HStack>
    </VStack>
  );
}
